import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';
import { applyCoupon as applyCouponFirestore } from '../utils/couponUtils';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [cartLoading, setCartLoading] = useState(true);
  const { currentUser } = useAuth();

  const loadCart = useCallback(async () => {
    try {
      setCartLoading(true);
      if (!currentUser) {
        // If not logged in, ensure empty cart and coupon
        setCartItems([]);
        setAppliedCoupon(null);
        return;
      }

      // Load from Firestore for logged-in users
      const cartDoc = await getDoc(doc(db, 'carts', currentUser.uid));
      if (cartDoc.exists()) {
        const cartData = cartDoc.data();
        if (Array.isArray(cartData.items)) {
          setCartItems(cartData.items);
        } else {
          setCartItems([]);
        }
        setAppliedCoupon(cartData.appliedCoupon || null);
      } else {
        // Initialize an empty cart document lazily when first save happens
        setCartItems([]);
        setAppliedCoupon(null);
      }
    } catch (error) {
      console.error('Error loading cart:', error);
      // On error, keep cart empty to avoid stale state
      setCartItems([]);
      setAppliedCoupon(null);
    } finally {
      setCartLoading(false);
    }
  }, [currentUser]);

  const saveCart = useCallback(async () => {
    try {
      if (!currentUser || cartLoading) return; // Guests cannot save cart; don't save during initial load

      // Save to Firestore for logged-in users
      await setDoc(doc(db, 'carts', currentUser.uid), {
        items: cartItems,
        appliedCoupon: appliedCoupon,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error saving cart:', error);
    }
  }, [currentUser, cartItems, appliedCoupon, cartLoading]);

  // Load cart from localStorage/Firestore on mount
  useEffect(() => {
    loadCart();
  }, [currentUser, loadCart]);

  // Save cart to Firestore whenever it changes, after initial load completes
  useEffect(() => {
    if (!cartLoading) {
      saveCart();
    }
  }, [saveCart, cartLoading]);

  // Add item to cart
  const addToCart = (product, quantity = 1, selectedSize = null) => {
    if (!currentUser) {
      toast.error('Please login to add items to cart');
      return;
    }
    const cartItem = {
      id: product.id,
      title: product.title,
      price: product.discountedPrice || product.price,
      originalPrice: product.price,
      discountedPrice: product.discountedPrice,
      images: product.images,
      sku: product.sku,
      quantity,
      selectedSize,
      brand: product.brand
    };

    setCartItems(prevItems => {
      const existingItem = prevItems.find(
        item => item.id === product.id && item.selectedSize === selectedSize
      );

      if (existingItem) {
        toast.success('Quantity updated in cart');
        return prevItems.map(item =>
          item.id === product.id && item.selectedSize === selectedSize
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        toast.success('Added to cart');
        return [...prevItems, cartItem];
      }
    });
  };

  // Remove item from cart
  const removeFromCart = (productId, selectedSize = null) => {
    if (!currentUser) {
      toast.error('Please login first');
      return;
    }
    setCartItems(prevItems => 
      prevItems.filter(item => 
        !(item.id === productId && item.selectedSize === selectedSize)
      )
    );
    toast.success('Removed from cart');
  };

  // Update quantity
  const updateQuantity = (productId, selectedSize, newQuantity) => {
    if (!currentUser) {
      toast.error('Please login first');
      return;
    }
    if (newQuantity <= 0) {
      removeFromCart(productId, selectedSize);
      return;
    }

    setCartItems(prevItems =>
      prevItems.map(item =>
        item.id === productId && item.selectedSize === selectedSize
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };

  // Clear cart
  const clearCart = () => {
    if (!currentUser) {
      toast.error('Please login first');
      return;
    }
    setCartItems([]);
    setAppliedCoupon(null);
    toast.success('Cart cleared');
  };

  // Apply coupon (updates Firestore usage count)
  const applyCoupon = async (coupon) => {
    if (!currentUser) {
      toast.error('Please login to apply coupons');
      return { success: false, error: 'Please login to apply coupons' };
    }
    // Update Firestore usage count
    const result = await applyCouponFirestore(coupon.code, currentUser.uid);
    if (result.success) {
      setAppliedCoupon(coupon);
      toast.success(`Coupon "${coupon.code}" applied!`);
      return { success: true };
    } else {
      toast.error(result.error || 'Failed to apply coupon');
      return { success: false, error: result.error };
    }
  };

  // Remove coupon
  const removeCoupon = () => {
    if (!currentUser) {
      toast.error('Please login first');
      return;
    }
    setAppliedCoupon(null);
    toast.success('Coupon removed');
  };

  // Calculate totals
  const subtotal = Array.isArray(cartItems) ? cartItems.reduce((total, item) => total + (item.price * item.quantity), 0) : 0;
  
  const calculateDiscount = () => {
    if (!appliedCoupon) return 0;
    
    if (appliedCoupon.type === 'percentage') {
      return subtotal * (appliedCoupon.value / 100);
    } else {
      return appliedCoupon.value;
    }
  };

  const discount = calculateDiscount();
  const shipping = subtotal > 999 ? 0 : 99; // Free shipping above ₹999
  const total = subtotal - discount + shipping;

  // Get cart count
  const cartCount = Array.isArray(cartItems) ? cartItems.reduce((count, item) => count + item.quantity, 0) : 0;

  const value = {
    cartItems,
    appliedCoupon,
    cartLoading,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    applyCoupon,
    removeCoupon,
    subtotal,
    discount,
    shipping,
    total,
    cartCount
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};